import { Clock, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

function timeAgo(createdat) {
  const ts = Number(createdat || 0);
  if (!ts) return "";
  const ms = Date.now() - ts;
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.floor(h / 24);
  if (d === 1) return "yesterday";
  return `${d}d ago`;
}

function initialsFor(u) {
  const f = (u?.firstname || "").trim();
  const l = (u?.lastname || "").trim();
  if (f && l) return `${f[0]}${l[0]}`.toUpperCase();
  return (f || l || "?").slice(0, 2).toUpperCase();
}

function Section({
  title,
  subtitle,
  emptyText,
  buttonLabel,
  deals,
  totalCount,
  findAgent,
  onOpen,
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-black/[0.04] mb-5">
      <div className="px-6 py-4 border-b border-black/[0.06] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="w-7 h-7 bg-amber-100 text-amber-700 rounded-md inline-flex items-center justify-center">
            <Clock size={14} strokeWidth={2.2} />
          </span>
          <div>
            <div className="text-[15px] font-semibold text-slate-900 flex items-center gap-2">
              {title}
              {deals.length > 0 && (
                <span className="bg-amber-100 text-amber-800 text-[11px] font-bold rounded-full px-2 py-0.5">
                  {deals.length}
                </span>
              )}
            </div>
            <div className="text-[11.5px] text-gray-500 mt-0.5">{subtitle}</div>
          </div>
        </div>
      </div>

      {deals.length === 0 ? (
        <div className="px-6 py-10 text-center text-gray-400 text-[13px]">
          {emptyText}
        </div>
      ) : (
        <div>
          {deals.map((deal) => {
            const agent = findAgent(deal);
            const agentName = agent
              ? `${agent.firstname || ""} ${agent.lastname || ""}`.trim()
              : "Unknown agent";
            const subject =
              deal?.propertyaddress ||
              (deal?.dealtype ? `${deal.dealtype} request` : "Untitled deal");
            return (
              <div
                key={deal.createdat || deal.id}
                className="px-6 py-3.5 border-b border-slate-100 last:border-b-0 flex items-center gap-3 hover:bg-gray-50 transition"
              >
                <div className="w-9 h-9 rounded-full bg-slate-700 text-white text-[11.5px] font-bold flex items-center justify-center flex-shrink-0">
                  {initialsFor(agent)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13.5px] font-semibold text-slate-900 truncate">
                    {subject}
                  </div>
                  <div className="text-[11.5px] text-gray-500 flex items-center gap-2 mt-0.5">
                    <span className="truncate">{agentName}</span>
                    {deal?.dealtype && (
                      <span className="text-[10px] uppercase tracking-wider font-semibold bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                        {deal.dealtype}
                      </span>
                    )}
                    <span className="text-gray-400">
                      · {timeAgo(deal.createdat)}
                    </span>
                  </div>
                </div>
                <Link
                  to={`/dashboard/deals-detail/${deal.createdat}`}
                  className="text-[12px] font-semibold text-slate-700 hover:text-slate-900 px-2 py-1 rounded border border-slate-300 hover:bg-white no-underline"
                >
                  View
                </Link>
                <button
                  type="button"
                  onClick={() => onOpen?.(deal)}
                  className="text-[12px] font-semibold text-white bg-slate-900 hover:bg-slate-800 px-3 py-1.5 rounded flex items-center gap-1 cursor-pointer"
                >
                  {buttonLabel}
                  <ChevronRight size={13} />
                </button>
              </div>
            );
          })}
          {totalCount > deals.length && (
            <div className="px-6 py-3 text-center border-t border-slate-100">
              <Link
                to="/dashboard/deals"
                className="text-[12px] font-semibold text-slate-600 hover:text-slate-900 no-underline"
              >
                View all {totalCount} →
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PendingInboxPanel({
  deals,
  users,
  currentUser,
  onOpen,
  onCompleteFileRequest,
}) {
  const role = currentUser?.role;
  if (role !== "Admin" && role !== "Supervisor") return null;

  const allPending = (deals || [])
    .filter((d) => d?.status === "Pending")
    .sort((a, b) => Number(b?.createdat || 0) - Number(a?.createdat || 0));

  const turnIns = allPending.filter((d) => d?.requestType === "saleTurnIn");
  const fileRequests = allPending.filter(
    (d) => d?.requestType !== "saleTurnIn",
  );

  const findAgent = (deal) =>
    (users || []).find(
      (u) => u?.id === deal?.agentData || u?.id === deal?.createdby,
    );

  const showFileRequests = role === "Supervisor";

  return (
    <>
      {showFileRequests && (
        <Section
          title="Pending File Creation"
          subtitle="Click to notify the requesting agent that the file is complete"
          emptyText="No listings waiting for file creation."
          buttonLabel="Mark Completed"
          deals={fileRequests.slice(0, 8)}
          totalCount={fileRequests.length}
          findAgent={findAgent}
          onOpen={onCompleteFileRequest || onOpen}
        />
      )}
      <Section
        title="Pending Approval"
        subtitle="Sale turn-ins · click to complete commission and approve"
        emptyText="No deals waiting for approval."
        buttonLabel="Review & Approve"
        deals={turnIns.slice(0, 8)}
        totalCount={turnIns.length}
        findAgent={findAgent}
        onOpen={onOpen}
      />
    </>
  );
}

export default PendingInboxPanel;
